import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Wrench, Briefcase, Store, Search, Sparkles, TrendingUp, Truck, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { fetchProducts, fetchServices, fetchShops, fetchHiring, fetchCategories } from "@/lib/api";
import { ProductCard, ServiceCard, ShopCard, HireCard } from "@/components/Cards";
import { withinKm } from "@/components/JobFilters";

const NEAR_KM = 10;
const AREA_KM = 25;
// Area has listings only if something is in that PIN or within the active radius (default 25 km).
const hasNearby = (lists, km) => lists.some((l) => l.some((x) => withinKm(x, km)));
import { useAreaResolver, AreaChoices, AreaInput } from "@/components/AreaSearchBox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RadiusFilter from "@/components/RadiusFilter";
import SellInVaranasi from "@/components/SellInVaranasi";
import VaranasiSpotlight from "@/components/VaranasiSpotlight";
import { useTranslation } from "react-i18next";
import VoiceMic from "@/components/VoiceMic";
import { usePinCounts } from "@/lib/usePinCounts";

const iconMap = { ShoppingBag, Wrench, Briefcase, Store };

const HERO_IMG = "https://images.unsplash.com/photo-1489450278009-822e9be04dff?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const Section = ({ eyebrow, title, hindi, cta, children, testid }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid={testid}>
    <div className="flex items-end justify-between gap-4 mb-6 lg:mb-8">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-emerald-700 mb-1.5">{eyebrow}</div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">{title}</h2>
        {hindi && <div className="text-sm text-slate-500 mt-1 font-medium">{hindi}</div>}
      </div>
      {cta && <Link to={cta.to} data-testid={`${testid}-cta`} className={`${cta.mobile ? "inline-flex flex-shrink-0 whitespace-nowrap" : "hidden sm:inline-flex"} items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-900`}>{cta.label} →</Link>}
    </div>
    {children}
  </section>
);

export default function Home() {
  const navigate = useNavigate();
  const { pincode, radiusKm, setPincode } = useApp();
  const pinCounts = usePinCounts(pincode, radiusKm);
  const area = useAreaResolver(setPincode);
  const [areaText, setAreaText] = useState(pincode || "");
  const [noService, setNoService] = useState(false);
  const checkEmpty = useRef(false);
  const areaRef = useRef(null);
  useEffect(() => { setAreaText(pincode || ""); }, [pincode]);
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [shops, setShops] = useState([]);
  const [hiring, setHiring] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      fetchCategories(),
      fetchProducts({ pincode, radius_km: radiusKm ?? undefined, limit: 10 }),
      fetchServices({ pincode, radius_km: radiusKm ?? undefined, limit: 8 }),
      fetchShops({ pincode, radius_km: radiusKm ?? undefined, limit: 8 }),
      fetchHiring(pincode ? { pincode, radius_km: NEAR_KM, limit: 50 } : { limit: 6 }),
    ]).then(([cats, prods, srvs, shs, hirs]) => {
      if (!mounted) return;
      setCategories(cats);
      setProducts(prods);
      setServices(srvs);
      setShops(shs);
      const near = pincode ? hirs.filter((j) => withinKm(j, NEAR_KM)).slice(0, 8) : hirs;
      setHiring(near);
      if (checkEmpty.current) { checkEmpty.current = false; if (!hasNearby([prods, srvs, shs, near], radiusKm ?? AREA_KM)) setNoService(true); }
    }).catch(() => {}).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [pincode, radiusKm]);

  const runSearch = (text, pin = pincode) => {
    const params = new URLSearchParams();
    if (text) params.set("q", text);
    if (pin) params.set("pincode", pin);
    navigate(`/search?${params.toString()}`);
  };
  // Area picked without a keyword: stay on Home; the reloaded sections decide the empty-area dialog.
  const afterArea = (pin) => {
    if (!pin) return;
    if (q.trim()) { runSearch(q.trim(), pin); return; }
    if (pin === pincode && !loading) { if (!hasNearby([products, services, shops, hiring], radiusKm ?? AREA_KM)) setNoService(true); }
    else checkEmpty.current = true;
  };
  const submitSearch = async (e) => {
    e.preventDefault();
    const typed = areaText.trim();
    if (typed && typed !== pincode) { afterArea(await area.resolve(typed)); return; }
    if (q.trim() || !pincode) { runSearch(q.trim()); return; }
    afterArea(pincode);
  };
  // Voice: final speech result fills the box (still editable) and runs the search once.
  const voiceSearch = (txt) => { const t = txt.replace(/[।.!?,]+$/u, "").trim(); if (!t) return; setQ(t); runSearch(t); };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" data-testid="home-top-search">
      <form onSubmit={submitSearch} className="bg-white border border-slate-200 rounded-2xl p-2 shadow-[0_20px_50px_-12px_rgba(4,120,87,0.15)] max-w-3xl" data-testid="hero-search-form">
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <div ref={areaRef} className="flex-1 flex min-w-0"><AreaInput value={areaText} onChange={(v) => { setAreaText(v); area.clear(); }} busy={area.busy} /></div>
          <div className="hidden sm:block w-px bg-slate-200 mx-1 my-2" />
          <div className="flex-[2] flex items-center gap-2 px-3 py-2.5 min-w-0">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("nav.search")}
              className="w-full bg-transparent outline-none text-sm sm:text-base placeholder:text-slate-400 min-w-0"
              data-testid="hero-search-input"
            />
            <VoiceMic onText={voiceSearch} showUnsupported unsupportedMsg="Voice search isn't available in this browser — please type and tap Search." testid="hero-search-mic" hint />
          </div>
          <button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-6 py-3 font-semibold text-sm transition-colors" data-testid="hero-search-btn">
            Search
          </button>
        </div>
        <AreaChoices choices={area.choices} error={area.error} onChoose={async (r) => afterArea(await area.choose(r))} />
      </form>
      </div>
      <Dialog open={noService} onOpenChange={setNoService}>
        <DialogContent className="max-w-sm" data-testid="no-service-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Service not available in this area</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">PIN <b>{pincode}</b> ke aas-paas abhi koi shop, service ya job nahi mili. Doosra PIN ya area try karein.</p>
          <button type="button" onClick={() => { setNoService(false); setAreaText(""); setTimeout(() => areaRef.current?.querySelector("input")?.focus(), 50); }} className="mt-2 rounded-full bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white px-5 py-2.5 text-sm font-semibold" data-testid="no-service-try-another">Doosra area try karein</button>
        </DialogContent>
      </Dialog>
      <SellInVaranasi />
      <VaranasiSpotlight />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14 lg:pt-14 lg:pb-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-full px-3 py-1 text-xs font-semibold mb-5">
                <Sparkles className="w-3.5 h-3.5" /> India's local marketplace, reimagined
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight">
                Apne aas-paas ka
                <span className="block text-emerald-700">bazaar, online.</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed">
                Products, services aur naukri — sab aapke pincode ke aas-paas.
                Local shops se lekar home services tak, ek hi jagah.
              </p>
              <nav aria-label="Categories" className="mt-5 flex flex-wrap gap-2" data-testid="hero-category-links">
                {categories.map((c) => {
                  const Icon = iconMap[c.icon] || ShoppingBag;
                  return (
                    <Link key={c.id} to={`/category/${c.id}`} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold min-h-[40px] transition-colors ${c.id === "hiring" ? "bg-emerald-700 border-emerald-700 text-white hover:bg-emerald-800" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-500 hover:text-emerald-800"}`} data-testid={`hero-category-${c.id}`}>
                      <Icon className="w-4 h-4" /> {c.id === "hiring" ? "Jobs / Naukri" : c.name}
                    </Link>
                  );
                })}
              </nav>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-emerald-700" /> Same-pincode delivery first</div>
                <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Verified local vendors</div>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-emerald-700/20">
                <img src={HERO_IMG} alt="Indian local market" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-emerald-900/60 via-transparent to-transparent" />
              </div>
              <div className="absolute -left-6 top-8 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 max-w-[220px] animate-fade-up" style={{ animationDelay: '150ms' }}>
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700"><TrendingUp className="w-4 h-4" /> Trending</div>
                <div className="mt-1 font-display font-bold text-slate-900">Fresh Atta from Sharma Store</div>
                <div className="text-xs text-slate-500 mt-1">₹495</div>
              </div>
              <div className="absolute -right-4 bottom-10 bg-emerald-700 text-white rounded-2xl shadow-xl p-4 max-w-[200px] animate-fade-up" style={{ animationDelay: '300ms' }}>
                <div className="text-xs uppercase tracking-wider opacity-80">Live nearby</div>
                <div className="font-display text-2xl font-bold mt-1">12 shops</div>
                <div className="text-xs opacity-90 mt-0.5">delivering to your pincode right now</div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Nearby radius (shared filter for the listings below) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 -mb-6 relative z-10" data-testid="home-radius-filter"><RadiusFilter /></div>

      {/* Popular Products */}
      <Section eyebrow="Aapke liye" title={t("home.popularProducts")} hindi="Aas-paas se sabse zyada order kiye jaane wale saaman" cta={{ to: "/category/products", label: "Browse all", mobile: true }} testid="popular-products-section">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
          {products.slice(0, 10).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </Section>

      {/* Popular Services */}
      <Section eyebrow="Ghar par sewa" title={t("home.popularServices")} hindi="Plumber, electrician, tuition — sab home par" cta={{ to: "/category/services", label: "All services", mobile: true }} testid="popular-services-section">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
          {services.slice(0, 8).map((s) => <ServiceCard key={s.id} service={s} />)}
        </div>
      </Section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4" data-testid="categories-section">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4">
          {categories.map((c, i) => {
            const Icon = iconMap[c.icon] || ShoppingBag;
            return (
              <Link key={c.id} to={`/category/${c.id}`} className="group relative bg-white border border-slate-100 rounded-2xl p-5 max-[359px]:p-4 card-hover animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`category-tile-${c.id}`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 bg-emerald-50 text-emerald-700 group-hover:bg-emerald-700 group-hover:text-white transition-colors`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display text-lg max-[359px]:text-base font-bold text-slate-900 whitespace-nowrap">{c.name}{pinCounts && pinCounts[c.id] !== undefined && <span className="font-normal text-sm text-slate-500" data-testid={`category-tile-count-${c.id}`}> · {pinCounts[c.id]}</span>}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.hindi}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Nearby Market strip */}
      <Section eyebrow="Nazdeeki bazaar" title={t("home.nearbyMarket")} hindi="Aapke pincode ke aas-paas ki live shops" cta={{ to: "/category/shops", label: "See all shops" }} testid="nearby-market-section">
        <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {shops.slice(0, 6).map((s) => (
            <div key={s.id} className="flex-shrink-0 w-[280px] sm:w-[320px]">
              <ShopCard shop={s} />
            </div>
          ))}
          {!loading && shops.length === 0 && (
            <div className="text-sm text-slate-500 py-6" data-testid="no-shops-msg">No shops in this area yet. Try increasing the radius or picking a different PIN.</div>
          )}
        </div>
      </Section>

      {/* Nearby Shops (grid) */}
      <Section eyebrow="Local heroes" title={t("home.nearbyShops")} hindi="Aapke ilake ki bharosemand dukaanein" cta={{ to: "/category/shops", label: "View all shops" }} testid="nearby-shops-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {shops.slice(0, 6).map((s) => <ShopCard key={s.id} shop={s} />)}
        </div>
      </Section>

      {/* Hiring: nearby strip once a PIN is chosen, else the general section */}
      {pincode ? (
        <Section eyebrow={`PIN ${pincode} ke paas`} title={`Jobs within ${NEAR_KM} km`} hindi="Aapke aas-paas ki naukriyan, sabse nazdeek pehle" cta={{ to: `/category/hiring?km=${NEAR_KM}`, label: "Sab dekhein" }} testid="nearby-jobs-section">
          {loading ? <div className="text-sm text-slate-500 py-6">Loading...</div> : hiring.length ? (
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-px-4 sm:scroll-px-0 pb-3 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="nearby-jobs-strip">
              {hiring.map((h) => <div key={h.id} className="snap-start flex-shrink-0 w-[85%] sm:w-[22rem]"><HireCard job={h} /></div>)}
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-6" data-testid="nearby-jobs-empty">{NEAR_KM} km ke andar abhi koi job nahi mili. Sabhi jobs dekhne ke liye "Sab dekhein" dabayein.</div>
          )}
          <Link to={`/category/hiring?km=${NEAR_KM}`} className="sm:hidden mt-2 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700" data-testid="nearby-jobs-see-all">Sab dekhein →</Link>
        </Section>
      ) : (
        <Section eyebrow="Naukri milegi" title={t("home.hiring")} hindi="Local shops aur ghar walon ke liye kaam" cta={{ to: "/category/hiring", label: "All jobs" }} testid="hiring-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {hiring.slice(0, 6).map((h) => <HireCard key={h.id} job={h} />)}
          </div>
        </Section>
      )}

      {/* Vendor CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="vendor-cta">
        <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-8 lg:p-14">
          <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-emerald-700/30 blur-3xl" />
          <div className="absolute -left-10 -top-10 w-72 h-72 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-amber-400 mb-3">For local vendors</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Apni dukaan ko <span className="text-emerald-400">online laao.</span>
            </h2>
            <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed max-w-lg">
              Zero commission for first 3 months. List your shop, add products,
              and start receiving orders from customers in your own pincode.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => navigate("/profile?setup=1")} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full px-6 py-3 font-semibold text-sm transition-colors" data-testid="become-seller-btn">
                Become a Seller
              </button>
              <button className="border border-white/20 hover:bg-white/10 text-white rounded-full px-6 py-3 font-semibold text-sm transition-colors" data-testid="learn-more-btn">
                Learn more
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
